/**
 * Zustaendigkeitsmodule (`responsibility_modules`) - Lesen und Schreiben der Verantwortung.
 *
 * Zweites Modul der Vorlagenflaeche neben `api/aufgabenVorlagen.ts`: die beiden Praefixe werden
 * serverseitig zwar von EINER Fabrik bedient (`backend/src/http/routes/zustaendigkeit.ts`, zwei
 * Router-Instanzen), sind aber zwei getrennte Ressourcen mit eigenen Tabellen, eigener
 * `rowVersion` und eigenen Rechten. Ein gemeinsames Frontend-Modul haette zwei Datenformen unter
 * einem Namen gefuehrt.
 *
 * ============================== GEMESSEN, NICHT ANGENOMMEN ==============================
 *
 * Jede Angabe stammt aus dem Backend-Quelltext dieses Arbeitsbaums (Projektregel: "Miss es, statt
 * es anzunehmen"):
 *
 *   Routen     Es gibt GENAU ZWEI: `GET /api/responsibility-modules` (Rechte `null`, Constraint 9)
 *              und `PATCH /api/responsibility-modules/:moduleId` (`manage_templates`) -
 *              `backend/src/http/endpunktMatrix.ts:118-124`. KEIN POST, KEIN DELETE, und `name`
 *              steht in KEINER Schreiballowlist. Module lassen sich also weder anlegen noch
 *              umbenennen noch loeschen; die vier Vorgabezeilen aus `migrations/005_domaene.sql:136`
 *              sind der vollstaendige Bestand. Siehe dazu den Kopfkommentar von
 *              `components/TaskTemplate/VorlagenDialog.tsx` ("Was diese Flaeche NICHT kann").
 *   Koerper    `{ rowVersion, ownerUserId?, deputyUserId? }` - anders als bei den uebrigen
 *              PATCH-Routen des Hauses KEIN generisches `felder`-Objekt, sondern GENAU zwei benannte
 *              Felder (`routes/zustaendigkeit.ts:59-79`, dortiger Kommentar). Ein weggelassenes Feld
 *              bleibt unveraendert, ein ausdrueckliches `null` LOESCHT die Verantwortung - deshalb
 *              `string | null` und nicht `string | undefined`.
 *   Wirkung    Der PATCH schreibt nicht nur das Modul: er uebertraegt Hauptverantwortung UND
 *              Vertretung in DERSELBEN Transaktion auf alle OFFENEN Tasks des Moduls
 *              (`domaene/zustaendigkeit.ts#uebertrageVerantwortung`). Erledigte Tasks bleiben als
 *              Historie unangetastet, frei angelegte Tasks ohne Modulbezug ebenfalls. Die Antwort
 *              traegt die Zahl der angepassten Tasks als `uebertrageneTasks` NEBEN der Ressource.
 *              Diese Uebertragung ist also KEINE Aufgabe des Clients - der Bestand erledigte sie
 *              noch selbst (index.html:5667-5683), hier tut es der Server.
 *   Antwort    die Zeile durch `oeffentlich()`, also camelCase: `sort_index` -> `sortIndex`,
 *              `owner_user_id` -> `ownerUserId`, `row_version` -> `rowVersion`.
 */
import { anfrage } from "./basis.ts";

export const BASISPFAD = "/api/responsibility-modules";

/**
 * Ein Zustaendigkeitsmodul, wie `GET /api/responsibility-modules` es liefert.
 *
 * NICHT zu verwechseln mit `ResponsibilityModule` (`lib/types.ts`): jener Typ beschreibt die
 * Modulform des BESTANDS (`ownerName`/`deputyName` als freie Namen, alle Felder `unknown`, weil
 * `applyResponsibilityToTask` bewusst tolerant liest). Hier steht die Form der DATENBANK - die
 * Verantwortung haengt an Nutzer-IDs (`users(id)`, `migrations/005_domaene.sql:122f.`), nicht an
 * Namen.
 */
export interface Zustaendigkeitsmodul {
  id: string;
  name: string;
  sortIndex: number;
  ownerUserId: string | null;
  deputyUserId: string | null;
  rowVersion: number;
}

/** Die Antwort des PATCH: die Ressource PLUS die Zahl der mitgezogenen offenen Tasks. */
export interface ModulAntwort extends Zustaendigkeitsmodul {
  uebertrageneTasks: number;
}

/** Laedt alle Module, nach `sortIndex` sortiert (der Server ordnet bereits, siehe `ladeModule`). */
export function moduleLaden(signal?: AbortSignal): Promise<Zustaendigkeitsmodul[]> {
  return anfrage<Zustaendigkeitsmodul[]>(BASISPFAD, { signal });
}

/**
 * Schreibt Hauptverantwortung und Vertretung EINES Moduls. Beide Werte werden IMMER mitgeschickt -
 * auch `null` - damit das Leeren eines Feldes die Verantwortung tatsaechlich loescht, statt sie
 * (bei einem weggelassenen Schluessel) stehen zu lassen.
 *
 * Bei 409 wirft `anfrage()` einen `KonfliktFehler`, dessen `aktuellerStand` den Serverstand bereits
 * mitbringt - der Aufrufer braucht keine zweite Anfrage.
 */
export function modulSchreiben(
  moduleId: string,
  verantwortung: { ownerUserId: string | null; deputyUserId: string | null },
  rowVersion: number,
): Promise<ModulAntwort> {
  return anfrage<ModulAntwort>(`${BASISPFAD}/${encodeURIComponent(moduleId)}`, {
    methode: "PATCH",
    koerper: {
      rowVersion,
      ownerUserId: verantwortung.ownerUserId,
      deputyUserId: verantwortung.deputyUserId,
    },
  });
}
